<template>
  <div
    :class="[
      $style.container,
      {
        [$style.is24]: isIs24,
        [$style.hg]: isHg,
        ['layout-container']: isIs24,
      },
    ]"
  >
    <img :src="heroImageUrl" :alt="$t('landing.hero.imageAlt')" :class="$style.image" />
    <div
      :class="[
        {
          ['layout-container']: isHg,
          ['col-sm-8 col-md-6']: isIs24,
        },
        $style.box,
      ]"
    >
      <h1 :class="$style.heading">
        <slot name="title">
          {{ $t('landing.herobox.title') }}
        </slot>
      </h1>
      <div :class="$style.body">
        <slot name="body">
          {{ $t('landing.herobox.body') }}
        </slot>
      </div>
      <HgBadge v-if="showBadge" :theme="isIs24 ? 'pink' : 'secondary'" :class="$style.badgeWrapper">
        <HgIcon icon="TrendingUp" :theme="isIs24 ? 'primary' : 'secondary'" size="small" />
        <span>{{ $t('landing.herobox.subtitle') }}</span>
      </HgBadge>
      <div :class="$style.propertyTypeWrapper">
        <div :class="$style.propertyTypeBox" @click="onClickHouseButton">
          <img
            loading="lazy"
            :src="getPropertyTypeImage(PropertyType.House)"
            :alt="$t('landing.herobox.valuatehouseAlt')"
          />
          <span :class="$style.propertyTypeText">
            {{ $t('landing.herobox.valuatehouse') }}
          </span>
        </div>
        <div :class="$style.propertyTypeBox" @click="onClickApartmentButton">
          <img
            loading="lazy"
            :src="getPropertyTypeImage(PropertyType.Apartment)"
            :alt="$t('landing.herobox.valuateapartmentAlt')"
          />
          <span :class="$style.propertyTypeText">
            {{ $t('landing.herobox.valuateapartment') }}
          </span>
        </div>
      </div>
      <p :class="$style.howLink">
        {{ $t('landing.herobox.how.question') }}
        <a @click="onClickHowLink">
          {{ $t('landing.herobox.myProperties.cta') }}
        </a>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { HgBadge, HgIcon } from '@smg-real-estate/domus';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import propertyTypeHgApartment from '@/assets/images/landing-hero/icon-apartment-hg.svg?url';
import propertyTypeIs24Apartment from '@/assets/images/landing-hero/icon-apartment-is24.svg?url';
import propertyTypeHgHouse from '@/assets/images/landing-hero/icon-house-hg.svg?url';
import propertyTypeIs24House from '@/assets/images/landing-hero/icon-house-is24.svg?url';
import { getCurrentPlatform, isPlatformHg, isPlatformIs24 } from '@/helpers/Platform/Platform';
import { getStaticAssetsBaseUrl } from '@/helpers/Url/Url';
import { OVERVIEW_PAGE_PATH, VALUATION_FUNNEL_PATH } from '@/router/routes/constants';
import { usePropertyValuationStore } from '@/stores/propertyValuation';
import { trackingClickEvent, trackingLandingPageButtonEvent } from '@/tracking/tracking';
import {
  LANDING_PAGE_BUTTON_ACTION,
  LANDING_PAGE_BUTTON_EVENT,
  TRACKING_ACTION,
  TRACKING_LABEL,
} from '@/tracking/types';
import { PropertyType } from '@/types';

const propertyTypeImages = {
  house: {
    hg: propertyTypeHgHouse,
    is24: propertyTypeIs24House,
  },
  apartment: {
    hg: propertyTypeHgApartment,
    is24: propertyTypeIs24Apartment,
  },
};

const {
  heroImageUrl = `${getStaticAssetsBaseUrl()}images/landing/preload-hero-image.webp`,
  showBadge = true,
} = defineProps<{
  heroImageUrl?: string;
  showBadge?: boolean;
}>();

// Composables
const router = useRouter();
const route = useRoute();
const propertyValuationStore = usePropertyValuationStore();
const { locale } = useI18n();

// Component data
const platform = getCurrentPlatform();
const isIs24 = isPlatformIs24();
const isHg = isPlatformHg();

// Methods
const initData = (propertyType: PropertyType) => {
  propertyValuationStore.initData(propertyType);
};

const getPropertyTypeImage = (propertyType: PropertyType) => {
  return propertyTypeImages[propertyType][platform];
};

const onClickApartmentButton = () => {
  trackingLandingPageButtonEvent(
    LANDING_PAGE_BUTTON_ACTION.SELECT_PROPERTY_TYPE,
    LANDING_PAGE_BUTTON_EVENT.APARTMENT,
  );
  goToPropertyValuation(PropertyType.Apartment);
};

const onClickHouseButton = () => {
  trackingLandingPageButtonEvent(
    LANDING_PAGE_BUTTON_ACTION.SELECT_PROPERTY_TYPE,
    LANDING_PAGE_BUTTON_EVENT.HOUSE,
  );
  goToPropertyValuation(PropertyType.House);
};

const goToPropertyValuation = (propertyType: PropertyType) => {
  initData(propertyType);
  router.push({
    path: `${VALUATION_FUNNEL_PATH[locale.value]}`,
    ...(Object.keys(route.query).length && { query: route.query }),
  });
};

const onClickHowLink = (event: MouseEvent) => {
  event.preventDefault();

  trackingClickEvent(TRACKING_ACTION.GO_TO_MY_REAL_ESTATE, TRACKING_LABEL.VALUATION_FUNNEL_LINK);

  window.location.href = `${OVERVIEW_PAGE_PATH[locale.value]}?source=valuation_funnel_landing`;
};
</script>

<style lang="scss" module>
@use '@/assets/variables' as *;

$bg-img-height-s-is24: 322px;
$bg-img-height-l-is24: 600px;
$bg-img-height-s-hg: 264px;
$bg-img-height-l-hg: 344px;

@mixin hero-image {
  display: block;
  width: 100%;
  padding: 0;
  object-fit: cover;

  @include grid-media-query(sm) {
    margin-left: auto;
    border-radius: $border-radius-l;
  }
}

@mixin infobox {
  box-sizing: border-box;
  padding: $space-100;
  margin: 0 $space-100;
  background: $color-white;
  border-radius: $border-radius-l;
  box-shadow: $elevation-medium;

  @include grid-media-query(sm) {
    padding: $space-250 $space-200;
  }
}

.container {
  position: relative;

  &.hg {
    .image {
      @include hero-image;

      & {
        height: $bg-img-height-s-hg;

        @include grid-media-query(sm) {
          height: $bg-img-height-l-hg;
          border-radius: 0;
        }
      }
    }

    .box {
      @include infobox;

      & {
        position: relative;
        margin-top: calc($bg-img-height-s-hg * -1 + $space-250);

        @include grid-media-query(sm) {
          max-width: 680px;
          margin: 0 auto;
          margin-top: calc($bg-img-height-l-hg * -1 + $space-250);
        }
      }
    }
  }

  &.is24 {
    padding: 0;

    @include grid-media-query(sm) {
      padding-inline: $space-100;
      margin-bottom: 0;
    }

    .image {
      @include hero-image;

      & {
        height: $bg-img-height-s-is24;

        @include grid-media-query(sm) {
          width: calc(100% - 308px);
          height: $bg-img-height-l-is24;
        }
      }
    }

    .box {
      @include infobox;

      & {
        position: relative;
        width: auto;
        margin: calc($space-1000 + $space-200) * -1 $space-100 0 $space-100;

        @include grid-media-query(sm) {
          position: absolute;
          top: 50%;
          max-width: 566px;
          padding: $space-200;
          margin: 0 0 0 $space-400;
          transform: translateY(-50%);
        }
      }
    }
  }
}

.heading {
  margin-top: 0;
  margin-bottom: $space-100;

  @include heading-2;

  @include grid-media-query(sm) {
    @include heading-1;
  }
}
.body {
  @include body-2;

  margin-bottom: $space-100;
}

.badgeWrapper {
  max-width: 237px;
  max-height: 30px;
  display: inline-flex;
  align-items: center;
  gap: $space-50;

  span {
    display: inline-block;
    @include body-3-bold;
  }
}

.propertyTypeWrapper {
  display: flex;
  gap: $space-150;
  margin-top: $space-150;
}

.propertyTypeBox {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: $space-50;
  align-items: center;
  padding: $space-100;
  text-align: center;
  cursor: pointer;
  border-radius: $border-radius;
  box-shadow: $elevation-minimal;

  @include body-2-bold($color-black);

  > img {
    width: 100px;
    height: 100px;
  }

  &:hover,
  &:focus {
    box-shadow: $elevation-medium;
  }
}

.propertyTypeText {
  white-space: pre-line;
  text-align: center;
}

.howLink {
  @include body-3;
  cursor: default;
  margin-top: $space-100;
  margin-bottom: 0;
  a {
    cursor: pointer;

    &:hover {
      text-decoration: underline;
    }
  }
}
</style>
